import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Opportunity } from "@sales-ops-amplify/shared";
import { useOpportunities } from "@/hooks/useOpportunities";
import { useSalesMovesByDeals } from "@/hooks/useSalesMoves";
import {
  useDebouncedValue,
  useListPagination,
  paginationDisplay,
} from "@/hooks/usePagination";
import { PaginationBar } from "@/components/pagination-bar";
import { updateOpportunity } from "@/api/opportunities";
import { listDealPipelines } from "@/api/deal-pipelines";
import { getAnalyticsSummary } from "@/api/analytics";
import { cn } from "@/cn";

type Layout = "table" | "board";
type MoveFilter =
  | ""
  | "needs_action"
  | "at_risk"
  | "no_recommended_move"
  | "no_recent_activity";

function formatMoney(amount?: number, currency?: string): string {
  if (amount == null || !Number.isFinite(amount)) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return String(amount);
  }
}

function formatDate(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function parseMoveFilter(raw: string | null): MoveFilter {
  if (
    raw === "needs_action" ||
    raw === "at_risk" ||
    raw === "no_recommended_move" ||
    raw === "no_recent_activity"
  ) {
    return raw;
  }
  return "";
}

export function DealsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();
  const [layout, setLayout] = useState<Layout>("table");
  const [search, setSearch] = useState(
    () => searchParams.get("q") ?? searchParams.get("search") ?? "",
  );
  const [stage, setStage] = useState(() => searchParams.get("stage") ?? "");
  const [pipelineConfigId, setPipelineConfigId] = useState("");
  const [ownerUserId, setOwnerUserId] = useState("");
  const [closeDateFrom, setCloseDateFrom] = useState("");
  const [closeDateTo, setCloseDateTo] = useState("");
  const [moveFilter, setMoveFilter] = useState<MoveFilter>(() =>
    parseMoveFilter(searchParams.get("moveFilter")),
  );
  const debouncedSearch = useDebouncedValue(search.trim(), 300);

  useEffect(() => {
    setMoveFilter(parseMoveFilter(searchParams.get("moveFilter")));
    setStage(searchParams.get("stage") ?? "");
    setSearch(searchParams.get("q") ?? searchParams.get("search") ?? "");
  }, [searchParams]);

  const pipelinesQuery = useQuery({
    queryKey: ["deal-pipelines"],
    queryFn: listDealPipelines,
    staleTime: 60_000,
  });

  const analyticsQuery = useQuery({
    queryKey: ["analytics", "summary"],
    queryFn: getAnalyticsSummary,
    staleTime: 30_000,
  });

  const pipelines = pipelinesQuery.data?.pipelines ?? [];
  const selectedPipeline =
    pipelines.find((p) => p.pipelineConfigId === pipelineConfigId) ??
    pipelines.find((p) => p.isDefault) ??
    pipelines[0];
  const stages = selectedPipeline?.stages ?? [];

  const filterKey = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      stage: stage || undefined,
      pipelineConfigId: pipelineConfigId || undefined,
      ownerUserId: ownerUserId || undefined,
      closeDateFrom: closeDateFrom || undefined,
      closeDateTo: closeDateTo || undefined,
    }),
    [
      debouncedSearch,
      stage,
      pipelineConfigId,
      ownerUserId,
      closeDateFrom,
      closeDateTo,
    ],
  );

  const pagination = useListPagination([
    filterKey.search,
    filterKey.stage,
    filterKey.pipelineConfigId,
    filterKey.ownerUserId,
    filterKey.closeDateFrom,
    filterKey.closeDateTo,
    layout,
  ]);

  const { data, isLoading, error } = useOpportunities({
    ...filterKey,
    ...(layout === "table"
      ? { limit: pagination.limit, offset: pagination.offset }
      : { limit: 500, offset: 0 }),
  });

  const opportunitiesRaw = data?.opportunities ?? [];
  const dealIds = useMemo(
    () =>
      opportunitiesRaw.map((o) => o.opportunityId ?? o.id).filter(Boolean),
    [opportunitiesRaw],
  );
  const movesByDealQuery = useSalesMovesByDeals(dealIds, dealIds.length > 0);
  const movesByDeal = movesByDealQuery.data?.moves ?? {};

  const opportunities = useMemo(() => {
    const STALE_MS = 14 * 86_400_000;
    const now = Date.now();
    return opportunitiesRaw.filter((opp) => {
      const id = opp.opportunityId ?? opp.id;
      const move = movesByDeal[id];
      if (moveFilter === "needs_action") return Boolean(move);
      if (moveFilter === "at_risk") return move?.bucket === "AT_RISK";
      if (moveFilter === "no_recommended_move") return !move;
      if (moveFilter === "no_recent_activity") {
        const last = opp.lastActivityAt || opp.updatedAt;
        if (!last) return true;
        const t = new Date(last).getTime();
        return !Number.isNaN(t) && now - t >= STALE_MS;
      }
      return true;
    });
  }, [opportunitiesRaw, movesByDeal, moveFilter]);

  const total = data?.total ?? opportunitiesRaw.length;
  const display = paginationDisplay(total, pagination.page, pagination.pageSize);

  const owners = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of opportunities) {
      if (o.ownerUserId) {
        map.set(o.ownerUserId, o.ownerName || o.ownerUserId);
      }
    }
    return [...map.entries()];
  }, [opportunities]);

  const stageMutation = useMutation({
    mutationFn: ({
      id,
      stageName,
      stageId,
    }: {
      id: string;
      stageName: string;
      stageId?: string;
    }) =>
      updateOpportunity(id, {
        stage: stageName,
        stageId,
        pipelineConfigId: selectedPipeline?.pipelineConfigId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["opportunities"] });
      qc.invalidateQueries({ queryKey: ["analytics"] });
    },
  });

  const byStage = useMemo(() => {
    const map = new Map<string, Opportunity[]>();
    for (const s of stages) map.set(s.name, []);
    for (const opp of opportunities) {
      const key = opp.stage || stages[0]?.name || "Lead";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(opp);
    }
    return map;
  }, [opportunities, stages]);

  const metrics = analyticsQuery.data;
  const forecastCats = metrics?.dealsForecastByCategory ?? [];
  const stageVolume = metrics?.dealsStageVolume ?? [];

  return (
    <div className="mx-auto max-w-7xl">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Deals</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Track opportunities across your pipeline — table or kanban.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-md border border-border p-0.5">
            {(
              [
                ["table", "Table"],
                ["board", "Board"],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setLayout(id)}
                className={cn(
                  "rounded px-3 py-1 text-xs font-medium",
                  layout === id
                    ? "bg-brand text-brand-foreground"
                    : "text-muted-foreground",
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <Link
            to="/deals/add"
            className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-foreground"
          >
            Create deal
          </Link>
        </div>
      </header>

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-border px-3 py-2">
          <p className="text-xs text-muted-foreground">Open pipeline</p>
          <p className="text-lg font-semibold tabular-nums">
            {formatMoney(metrics?.openPipelineAmount)}
          </p>
        </div>
        <div className="rounded-lg border border-border px-3 py-2">
          <p className="text-xs text-muted-foreground">Weighted</p>
          <p className="text-lg font-semibold tabular-nums">
            {formatMoney(metrics?.weightedPipeline)}
          </p>
        </div>
        <div className="rounded-lg border border-border px-3 py-2">
          <p className="text-xs text-muted-foreground">Win rate</p>
          <p className="text-lg font-semibold tabular-nums">
            {metrics?.dealWinRate ?? metrics?.winRate ?? "—"}%
          </p>
        </div>
      </div>

      {(forecastCats.length > 0 || stageVolume.some((s) => s.count > 0)) && (
        <div className="mb-4 grid gap-3 lg:grid-cols-2">
          <div className="rounded-lg border border-border p-3">
            <p className="mb-2 text-xs font-medium uppercase text-muted-foreground">
              Forecast by category
            </p>
            <ul className="space-y-1 text-sm">
              {forecastCats.map((row) => (
                <li
                  key={row.category}
                  className="flex justify-between gap-2 tabular-nums"
                >
                  <span className="capitalize text-muted-foreground">
                    {row.category.replace("_", " ")}
                  </span>
                  <span>
                    {row.count} · {formatMoney(row.amount)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-border p-3">
            <p className="mb-2 text-xs font-medium uppercase text-muted-foreground">
              Volume by stage
            </p>
            <ul className="space-y-1 text-sm">
              {stageVolume
                .filter((s) => s.count > 0)
                .map((row) => (
                  <li
                    key={row.stage}
                    className="flex justify-between gap-2 tabular-nums"
                  >
                    <span className="text-muted-foreground">{row.stage}</span>
                    <span>
                      {row.count} · {formatMoney(row.amount)}
                    </span>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search deals…"
          className="min-w-[200px] flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm"
        />
        {pipelines.length > 1 ? (
          <select
            value={pipelineConfigId}
            onChange={(e) => {
              setPipelineConfigId(e.target.value);
              setStage("");
            }}
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          >
            <option value="">All pipelines</option>
            {pipelines.map((p) => (
              <option key={p.pipelineConfigId} value={p.pipelineConfigId}>
                {p.name}
              </option>
            ))}
          </select>
        ) : null}
        <select
          value={stage}
          onChange={(e) => setStage(e.target.value)}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        >
          <option value="">All stages</option>
          {stages.map((s) => (
            <option key={s.id} value={s.name}>
              {s.name}
            </option>
          ))}
        </select>
        {owners.length > 0 ? (
          <select
            value={ownerUserId}
            onChange={(e) => setOwnerUserId(e.target.value)}
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          >
            <option value="">All owners</option>
            {owners.map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        ) : null}
        <input
          type="date"
          value={closeDateFrom}
          onChange={(e) => setCloseDateFrom(e.target.value)}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          title="Close date from"
        />
        <input
          type="date"
          value={closeDateTo}
          onChange={(e) => setCloseDateTo(e.target.value)}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          title="Close date to"
        />
        <select
          value={moveFilter}
          onChange={(e) => {
            const next = e.target.value as MoveFilter;
            setMoveFilter(next);
            const nextParams = new URLSearchParams(searchParams);
            if (next) nextParams.set("moveFilter", next);
            else nextParams.delete("moveFilter");
            setSearchParams(nextParams, { replace: true });
          }}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        >
          <option value="">All move states</option>
          <option value="needs_action">Needs action</option>
          <option value="at_risk">At risk</option>
          <option value="no_recommended_move">No recommended move</option>
          <option value="no_recent_activity">No recent activity</option>
        </select>
      </div>

      {error ? (
        <p className="text-sm text-red-600">
          {error instanceof Error ? error.message : "Failed to load"}
        </p>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading deals…</p>
      ) : layout === "table" ? (
        <>
          <div className="min-w-0 overflow-x-auto overscroll-x-contain rounded-lg border border-border [-webkit-overflow-scrolling:touch]">
            <table className="w-full min-w-[960px] text-left text-sm">
              <thead className="border-b border-border bg-muted/30 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Deal</th>
                  <th className="px-3 py-2">Company</th>
                  <th className="px-3 py-2">Stage</th>
                  <th className="px-3 py-2">Amount</th>
                  <th className="px-3 py-2">Prob.</th>
                  <th className="px-3 py-2">Close date</th>
                  <th className="px-3 py-2">Owner</th>
                  <th className="px-3 py-2">Recommended move</th>
                  <th className="px-3 py-2">Next step</th>
                  <th className="px-3 py-2">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {opportunities.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-3 py-10 text-center text-muted-foreground"
                    >
                      No deals.{" "}
                      <Link
                        to="/deals/add"
                        className="text-brand hover:underline"
                      >
                        Create one
                      </Link>
                    </td>
                  </tr>
                ) : (
                  opportunities.map((opp) => (
                    <tr
                      key={opp.id}
                      className="cursor-pointer border-b border-border/70 hover:bg-muted/10"
                      onClick={() => navigate(`/deals/${opp.id}`)}
                    >
                      <td className="px-3 py-2.5 font-medium">
                        {opp.dealName}
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground">
                        {opp.company}
                      </td>
                      <td
                        className="px-3 py-2.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <select
                          className="rounded border border-border bg-background px-1 py-0.5 text-xs"
                          value={opp.stage ?? ""}
                          onChange={(e) => {
                            const name = e.target.value;
                            const def = stages.find((s) => s.name === name);
                            stageMutation.mutate({
                              id: opp.id,
                              stageName: name,
                              stageId: def?.id,
                            });
                          }}
                        >
                          <option value="">—</option>
                          {stages.map((s) => (
                            <option key={s.id} value={s.name}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2.5 tabular-nums">
                        {formatMoney(opp.amount, opp.currency)}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums text-muted-foreground">
                        {opp.probability != null ? `${opp.probability}%` : "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        {formatDate(opp.closeDate)}
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground">
                        {opp.ownerName || "—"}
                      </td>
                      <td className="max-w-[220px] px-3 py-2.5 text-muted-foreground">
                        {(() => {
                          const id = opp.opportunityId ?? opp.id;
                          const move = movesByDeal[id];
                          if (!move) return "—";
                          return (
                            <span
                              className={cn(
                                "line-clamp-2 text-xs",
                                move.priority === "critical" || move.priority === "high"
                                  ? "font-medium text-foreground"
                                  : "",
                              )}
                              title={move.headline}
                            >
                              {move.recommendedAction}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground">
                        {opp.nextStep || "—"}
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground">
                        {formatDate(opp.lastActivityAt)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <PaginationBar
            page={pagination.page}
            pageSize={pagination.pageSize}
            totalItems={total}
            totalPages={display.totalPages}
            showingFrom={display.showingFrom}
            showingTo={display.showingTo}
            onPageChange={pagination.setPage}
            onPageSizeChange={pagination.setPageSize}
          />
        </>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-4">
          {(stages.length
            ? stages.map((s) => s.name)
            : ["Lead", "Qualified", "Won", "Lost"]
          ).map((stageName) => {
            const cards = byStage.get(stageName) ?? [];
            return (
              <div
                key={stageName}
                className="w-72 shrink-0 rounded-lg border border-border bg-muted/10"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const id = e.dataTransfer.getData("text/deal-id");
                  if (!id) return;
                  const def = stages.find((s) => s.name === stageName);
                  stageMutation.mutate({
                    id,
                    stageName,
                    stageId: def?.id,
                  });
                }}
              >
                <div className="border-b border-border px-3 py-2">
                  <p className="text-sm font-semibold">{stageName}</p>
                  <p className="text-xs text-muted-foreground">
                    {cards.length} ·{" "}
                    {formatMoney(
                      cards.reduce((sum, c) => sum + (c.amount ?? 0), 0),
                    )}
                  </p>
                </div>
                <div className="space-y-2 p-2">
                  {cards.map((opp) => (
                    <button
                      key={opp.id}
                      type="button"
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData("text/deal-id", opp.id);
                      }}
                      onClick={() => navigate(`/deals/${opp.id}`)}
                      className="w-full rounded-md border border-border bg-card p-3 text-left shadow-sm hover:border-brand/40"
                    >
                      <p className="text-sm font-medium">{opp.dealName}</p>
                      <p className="text-xs text-muted-foreground">
                        {opp.company}
                      </p>
                      <p className="mt-2 text-xs tabular-nums">
                        {formatMoney(opp.amount, opp.currency)}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** @deprecated Use DealsPage */
export { DealsPage as OpportunitiesPage };
